export type HeadingElements = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export const headingIncrement = (
  heading: HeadingElements,
  increment: number = 1
) => {
  const level = parseInt(heading.replace('h', ''), 10);
  return `h${level + increment}` as HeadingElements;
};

/**
 * It extracts all non empty headings from html
 */
export const getNonEmptyHeadings = (html: string): number[] => {
  const headingRegex = /<h([1-6])\b[^>]*>(.*?)<\/h\1>/gi;
  let match: RegExpExecArray | null;
  const headingLevels: number[] = [];

  while ((match = headingRegex.exec(html)) !== null) {
    const headingText = match[2].trim();
    const headingLevel = parseInt(match[1], 10);
    if (headingText && !headingLevels.includes(headingLevel)) {
      // Ignore empty headings
      headingLevels.push(headingLevel);
    }
  }

  return headingLevels;
};

/**
 * It extracts all headings from html and shifts them according to the baseline
 *
 * i.e.
 * given headings h3, h5, h6, and baseline 2, it will return h2, h3, h4
 * given headings h3, h5, h6, and baseline 3, it will return h3, h4, h5
 * given headings h1, h2, h6, and baseline 3, it will return h3, h4, h5
 * given headings h1, h2, h6, and baseline 5, it will return h5, h6
 */
export const getRemappedHeadingsAccordingToBaseline = (
  html: string,
  baseline: number // 1 - 6
): Record<HeadingElements, HeadingElements> => {
  if (baseline < 1 || baseline > 6) {
    throw new Error('Baseline must be between 1 and 6');
  }
  const headingLevels = getNonEmptyHeadings(html);

  // Sorted deduplicated heading levels
  const sortedHeadingLevels = Array.from(new Set(headingLevels)).sort(
    (a, b) => a - b
  );

  return sortedHeadingLevels.reduce<Record<HeadingElements, HeadingElements>>(
    (result, level, index) => {
      const shiftedLevel = baseline + index;
      if (shiftedLevel > 6) {
        return result;
      }
      return {
        ...result,
        [`h${level}` as HeadingElements]: `h${shiftedLevel}` as HeadingElements,
      };
    },
    {} as Record<HeadingElements, HeadingElements>
  );
};
